import React, { memo, useRef } from 'react';
import Slider from 'react-slick';
import { ChevronLeft, ChevronRight, AutoAwesome } from '@mui/icons-material';
import { Button } from '@mui/material';

import generateValidUrl from 'core/fetch/generateValidUrl';
import { getProfileImage, isUserNew } from 'models/user/IUser';
import { IStrangerUser } from 'models/user/IStrangerUser/IStrangerUser';
import { IImage } from 'models/core/image/IImage';
import FSK18 from '../../../../assets/images/icons/fsk18.png';
import useTranslation from 'services/i18n/core/useTranslation';

export interface StrangerProfileHeroProps {
    pictures: IImage[];
    strangerUser: IStrangerUser | null;
    onPictureClick?: (index: number) => void;
}

export const StrangerProfileHero = memo(({ pictures, strangerUser, onPictureClick }: StrangerProfileHeroProps) => {
    const sliderRef = useRef<Slider | null>(null);
    const translation = useTranslation();

    const galleryPictures: IImage[] =
        pictures.length > 0
            ? pictures
            : strangerUser
              ? [{ Picture: getProfileImage(strangerUser), Coins: 0 }]
              : [];

    const settings = {
        dots: galleryPictures.length > 1,
        infinite: galleryPictures.length > 1,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        autoplay: false,
    };

    const showNewBadge = strangerUser ? isUserNew(strangerUser) : false;
    const isOnline = strangerUser?.IsOnline;

    const handleSlideClick = (index: number) => {
        onPictureClick?.(index);
    };

    return (
        <div className="stranger-profile__hero">
            {galleryPictures.length > 1 && (
                <>
                    <button
                        type="button"
                        className="stranger-profile__nav-arrow stranger-profile__nav-arrow--prev"
                        aria-label="Previous photo"
                        onClick={() => sliderRef.current?.slickPrev()}
                    >
                        <ChevronLeft fontSize="small" />
                    </button>
                    <button
                        type="button"
                        className="stranger-profile__nav-arrow stranger-profile__nav-arrow--next"
                        aria-label="Next photo"
                        onClick={() => sliderRef.current?.slickNext()}
                    >
                        <ChevronRight fontSize="small" />
                    </button>
                </>
            )}

            <div className="stranger-profile__hero-badges">
                {showNewBadge && (
                    <span className="stranger-profile__badge stranger-profile__badge--new">
                        <AutoAwesome sx={{ fontSize: 14 }} />
                        Neu hier
                    </span>
                )}
                {isOnline && (
                    <span className="stranger-profile__badge stranger-profile__badge--online">
                        <span className="stranger-profile__online-dot" />
                        Online
                    </span>
                )}
            </div>

            <Slider ref={sliderRef} {...settings}>
                {galleryPictures.map((image, index) => {
                    const isLocked = (image.Coins ?? 0) > 0;

                    return (
                        <div key={`${image.Picture}-${index}`}>
                            <div
                                className={`stranger-profile__hero-slide${isLocked ? ' stranger-profile__hero-slide--locked' : ''}${
                                    onPictureClick ? ' stranger-profile__hero-slide--clickable' : ''
                                }`}
                                onClick={() => handleSlideClick(index)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleSlideClick(index);
                                    }
                                }}
                                role={onPictureClick ? 'button' : undefined}
                                tabIndex={onPictureClick ? 0 : undefined}
                            >
                                <img
                                    className="stranger-profile__hero-image"
                                    src={generateValidUrl(image.Picture)}
                                    alt={`Profilbild ${index + 1}`}
                                />
                                {isLocked && (
                                    <div className="stranger-profile__hero-lock">
                                        <img className="stranger-profile__hero-lock-icon" src={FSK18} alt="" />
                                        <Button
                                            size="small"
                                            className="stranger-profile__hero-lock-btn"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleSlideClick(index);
                                            }}
                                        >
                                            {translation.DISCOVER}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </Slider>
        </div>
    );
});

export default StrangerProfileHero;
